import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Howl } from 'howler';

type UseAudioPlayerOptions = {
	src: string | string[];
	loop?: boolean;
	autoplay?: boolean;
	volume?: number;
	html5?: boolean;
	preload?: boolean;
	respectGlobalAudioSetting?: boolean;
};

type UseAudioPlayerReturn = {
	play: () => void;
	pause: () => void;
	stop: () => void;
	playing: boolean;
};

const useAudioPlayer = ({
	src,
	loop = false,
	autoplay = false,
	volume = 1,
	html5 = false,
	preload = true,
	respectGlobalAudioSetting = true,
}: UseAudioPlayerOptions): UseAudioPlayerReturn => {
	const [playing, setPlaying] = useState(false);
	const howlRef = useRef<Howl | null>(null);
	const resumeHandlerRef = useRef<(() => void) | null>(null);
	const pendingPlayRef = useRef(false);

	const globalAudioEnabled = useSelector(
		(state: any) => state.sharedReducer?.userData?.settings?.audio_on ?? true,
	);
	const audioEnabled = respectGlobalAudioSetting ? globalAudioEnabled : true;

	useEffect(() => {
		if (!audioEnabled) {
			setPlaying(false);
			return;
		}
		const sources = Array.isArray(src) ? src : [src];

		if (typeof window !== 'undefined') {
			const howler = (window as any).Howler;
			if (howler) {
				howler.autoUnlock = true;
				howler.autoSuspend = false;
			}
		}

		const howlInstance = new Howl({
			src: sources,
			loop,
			volume,
			html5,
			preload,
		});
		howlRef.current = howlInstance;

		const detachResumeHandlers = () => {
			const resumeHandler = resumeHandlerRef.current;
			if (resumeHandler && typeof window !== 'undefined') {
				window.removeEventListener('pointerdown', resumeHandler);
				window.removeEventListener('keydown', resumeHandler);
				resumeHandlerRef.current = null;
			}
		};

		const attachResumeHandlers = () => {
			if (resumeHandlerRef.current || typeof window === 'undefined') return;
			const handler = () => {
				if (!howlInstance.playing()) {
					howlInstance.play();
				}
				detachResumeHandlers();
			};
			resumeHandlerRef.current = handler;
			window.addEventListener('pointerdown', handler, { once: true });
			window.addEventListener('keydown', handler, { once: true });
		};

		const handlePlay = () => {
			pendingPlayRef.current = false;
			setPlaying(true);
			detachResumeHandlers();
		};

		const handlePauseOrStop = () => {
			setPlaying(false);
		};

		howlInstance.on('play', handlePlay);
		howlInstance.on('pause', handlePauseOrStop);
		howlInstance.on('stop', handlePauseOrStop);
		howlInstance.on('end', handlePauseOrStop);
		howlInstance.on('playerror', attachResumeHandlers);

		if (autoplay || pendingPlayRef.current) {
			pendingPlayRef.current = true;
			if (!howlInstance.playing()) {
				howlInstance.play();
			}
		}

		return () => {
			detachResumeHandlers();
			howlInstance.off('play', handlePlay);
			howlInstance.off('pause', handlePauseOrStop);
			howlInstance.off('stop', handlePauseOrStop);
			howlInstance.off('end', handlePauseOrStop);
			howlInstance.off('playerror', attachResumeHandlers);
			pendingPlayRef.current = false;
			if (howlInstance.playing()) {
				howlInstance.stop();
			}
			howlInstance.unload();
			howlRef.current = null;
			setPlaying(false);
		};
	}, [audioEnabled, autoplay, html5, loop, preload, src, volume]);

	const play = useCallback(() => {
		pendingPlayRef.current = true;
		if (!audioEnabled) {
			return;
		}
		const howl = howlRef.current;
		if (howl?.playing()) {
			pendingPlayRef.current = false;
			return;
		}
		if (!howl) {
			return;
		}
		howl.play();
	}, [audioEnabled]);

	const pause = useCallback(() => {
		pendingPlayRef.current = false;
		const howl = howlRef.current;
		if (!howl) return;
		if (howl.playing()) {
			howl.pause();
		}
	}, []);

	const stop = useCallback(() => {
		pendingPlayRef.current = false;
		const howl = howlRef.current;
		if (!howl) return;
		if (howl.playing()) {
			howl.stop();
		}
	}, []);

	return {
		play,
		pause,
		stop,
		playing,
	};
};

export default useAudioPlayer;
